import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  Optional,
  Self,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import * as moment from 'moment';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { differenceInCalendarDays } from 'date-fns';
import { DateFormatterDirective } from '../../directives/datetime';

@Component({
  selector: 'app-range-date-control',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DateFormatterDirective,
    NzGridModule,
    NzDatePickerModule,
  ],
  templateUrl: './range-date-control.component.html',
  styleUrls: ['./range-date-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RangeDateControlComponent implements ControlValueAccessor {
  @Input() public dateFormat = 'dd.MM.yyyy';
  @Input() public grid: [number, number] = [12, 12];
  @Input() public allowClear = true;
  @Input() public placeholder: string | [string, string] = '';
  @Input() public readonly = false;
  @Input() public disabled = false;

  public fromDate: Date | string | null = null;
  public toDate: Date | string | null = null;

  onChange: (value: [Date | string | null, Date | string | null]) => void =
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: () => void = () => {};

  get fromDatePlaceholder(): string {
    return Array.isArray(this.placeholder)
      ? this.placeholder[0]
      : this.placeholder;
  }

  get toDatePlaceholder(): string {
    return Array.isArray(this.placeholder)
      ? this.placeholder[1]
      : this.placeholder;
  }

  private readonly _cdr = inject(ChangeDetectorRef);

  constructor(@Self() @Optional() public control: NgControl) {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  public onFromChange(date: Date | null) {
    if (moment(date).isSame(moment(this.fromDate))) {
      return;
    }

    this.fromDate = date;
    this._emitValue();
  }

  public onToChange(date: Date | null) {
    if (moment(date).isSame(moment(this.toDate))) {
      return;
    }

    this.toDate = date;
    this._emitValue();
  }

  public disableFromDate = (current: Date): boolean => {
    if (!this.toDate || !current) {
      return false;
    }

    return differenceInCalendarDays(current, new Date(this.toDate)) > 0;
  };

  public disableToDate = (current: Date): boolean => {
    if (!this.fromDate || !current) {
      return false;
    }
    return (
      differenceInCalendarDays(current, new Date(this.fromDate)) < 0 ||
      differenceInCalendarDays(current, new Date()) > 0
    );
  };

  public writeValue(value: [Date | null, Date | null]): void {
    if (value) {
      this.fromDate = moment(value[0]).format('YYYY-MM-DD');
      this.toDate = moment(value[1]).format('YYYY-MM-DD');
    }
    this._cdr.markForCheck();
  }

  public registerOnChange(
    fn: (value: [Date | string | null, Date | string | null]) => void
  ): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private _emitValue() {
    if (!this.fromDate && !this.toDate) {
      this.onChange([null, null]);
      this._cdr.markForCheck();
      return;
    }
    console.log('s', this.fromDate, this.toDate);
    if (this.fromDate && this.toDate) {
      this.onChange([this.fromDate, this.toDate]);
    }
    this._cdr.markForCheck();
  }
}
